import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, H3, InputLabel, Switch, TextArea, TextInput, ValueListInput } from '@components/Common';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { Warning } from '@components/Common/SharedStyles';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';
import { isValidEmail } from '@helpers/parsing';
import { Customer, EditCustomerCmd, CreateCustomerCmd } from '@api/queries/customers';

const RemoveButton = styled(Button)`
  margin-right: 15px;
`;

const FieldGroup = styled.div`
  margin-bottom: 10px;
`;

const SectionTitle = styled.h4`
  margin: 20px 0 10px 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #ddd;
  color: #666;
  font-size: 0.9rem;
`;

interface CustomerEditorProps {
  customer: Customer | null;
  save: (data: EditCustomerCmd | CreateCustomerCmd) => void;
  close: () => void;
  remove?: () => void;
  isSaving: boolean;
  isRemoving?: boolean;
}

const CustomerEditor: React.FC<CustomerEditorProps> = ({
  customer,
  save,
  close,
  remove,
  isSaving,
  isRemoving = false
}) => {
  const { t } = useTranslation();
  const isEdit = customer !== null;

  // Core fields
  const [name, setName] = useState(customer?.name || '');
  const [customerNumber, setCustomerNumber] = useState(customer?.customerNumber || '');
  const [vatId, setVatId] = useState(customer?.vatId || '');
  const [streetAddress, setStreetAddress] = useState(customer?.streetAddress || '');
  const [postalCode, setPostalCode] = useState(customer?.postalCode || '');
  const [city, setCity] = useState(customer?.city || '');
  const [country, setCountry] = useState(customer?.country || '');
  const [primaryEmail, setPrimaryEmail] = useState(customer?.primaryEmail || '');
  const [isActive, setIsActive] = useState(customer?.isActive ?? true);

  // Configuration fields
  const [externalCustomerId, setExternalCustomerId] = useState(customer?.externalCustomerId || '');
  const [notes, setNotes] = useState(customer?.notes || '');
  const [setReferenceFromTruckScale, setSetReferenceFromTruckScale] = useState(customer?.setReferenceFromTruckScale || false);
  const [isSurfaceRadarNotificationEnabled, setIsSurfaceRadarNotificationEnabled] = useState(customer?.isSurfaceRadarNotificationEnabled || false);
  const [isEntrySendingEnabled, setIsEntrySendingEnabled] = useState(customer?.isEntrySendingEnabled || false);
  const [notificationEmails, setNotificationEmails] = useState<string[]>(customer?.notificationEmails || []);

  const isValid = () => {
    return name.trim().length >= 2;
  };

  const handleNotificationEmailsChanged = (newValues: string[]) => {
    const validEmails = newValues
      .filter(isValidEmail)
      .reduce((uniques, value) => (uniques.find(u => u === value) ? uniques : [...uniques, value]), [] as string[]);
    setNotificationEmails(validEmails);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      const editData: EditCustomerCmd = {
        id: customer.id,
        name,
        customerNumber,
        vatId,
        streetAddress,
        postalCode,
        city,
        country,
        primaryEmail,
        isActive,
        externalCustomerId,
        notes,
        setReferenceFromTruckScale,
        isSurfaceRadarNotificationEnabled,
        isEntrySendingEnabled,
        notificationEmails
      };
      save(editData);
    } else {
      const createData: CreateCustomerCmd = {
        name,
        customerNumber,
        vatId,
        streetAddress,
        postalCode,
        city,
        country,
        primaryEmail,
        externalCustomerId,
        notes,
        setReferenceFromTruckScale,
        isSurfaceRadarNotificationEnabled,
        isEntrySendingEnabled,
        notificationEmails
      };
      save(createData);
    }
  };

  const handleRemove = () => {
    if (remove && confirm(t('customer.confirm_delete'))) {
      remove();
    }
  };

  return (
    <SideForm onSubmit={handleSubmit} onEscape={close}>
      {isEdit ? (
        <H3>
          {t('editing')}: <i>{customer.name}</i>
        </H3>
      ) : (
        <H3>
          {t('add_new')} {t('customer.customer').toLowerCase()}
        </H3>
      )}

      <SectionTitle>{t('customer.basic_info')}</SectionTitle>

      <FieldGroup>
        <InputLabel htmlFor="name">{t('customer.name')} *</InputLabel>
        <TextInput
          id="name"
          name="name"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="customerNumber">{t('customer.customer_number')}</InputLabel>
        <TextInput
          id="customerNumber"
          name="customerNumber"
          type="text"
          value={customerNumber}
          onChange={e => setCustomerNumber(e.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="vatId">{t('customer.vat_id')}</InputLabel>
        <TextInput
          id="vatId"
          name="vatId"
          type="text"
          value={vatId}
          onChange={e => setVatId(e.target.value)}
        />
      </FieldGroup>

      <SectionTitle>{t('customer.address')}</SectionTitle>

      <FieldGroup>
        <InputLabel htmlFor="streetAddress">{t('customer.street_address')}</InputLabel>
        <TextInput
          id="streetAddress"
          name="streetAddress"
          type="text"
          value={streetAddress}
          onChange={e => setStreetAddress(e.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="postalCode">{t('customer.postal_code')}</InputLabel>
        <TextInput
          id="postalCode"
          name="postalCode"
          type="text"
          value={postalCode}
          onChange={e => setPostalCode(e.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="city">{t('customer.city')}</InputLabel>
        <TextInput
          id="city"
          name="city"
          type="text"
          value={city}
          onChange={e => setCity(e.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="country">{t('customer.country')}</InputLabel>
        <TextInput
          id="country"
          name="country"
          type="text"
          value={country}
          onChange={e => setCountry(e.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="primaryEmail">{t('customer.primary_email')}</InputLabel>
        <TextInput
          id="primaryEmail"
          name="primaryEmail"
          type="email"
          value={primaryEmail}
          onChange={e => setPrimaryEmail(e.target.value)}
        />
      </FieldGroup>

      <SectionTitle>{t('customer.settings')}</SectionTitle>

      <FieldGroup>
        <InputLabel htmlFor="externalCustomerId">{t('customer.external_customer_id')}</InputLabel>
        <TextInput
          id="externalCustomerId"
          name="externalCustomerId"
          type="text"
          value={externalCustomerId}
          onChange={e => setExternalCustomerId(e.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <Switch
          onChange={setIsEntrySendingEnabled}
          checked={isEntrySendingEnabled}
          id="isEntrySendingEnabled"
          label={t('customer.is_entry_sending_enabled')}
        />
      </FieldGroup>

      <FieldGroup>
        <Switch
          onChange={setSetReferenceFromTruckScale}
          checked={setReferenceFromTruckScale}
          id="setReferenceFromTruckScale"
          label={t('customer.set_reference_from_truck_scale')}
        />
      </FieldGroup>

      <FieldGroup>
        <Switch
          onChange={setIsSurfaceRadarNotificationEnabled}
          checked={isSurfaceRadarNotificationEnabled}
          id="isSurfaceRadarNotificationEnabled"
          label={t('customer.is_surface_radar_notification_enabled')}
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="notificationEmails">{t('customer.notification_emails')}</InputLabel>
        <ValueListInput
          isDisabled={!isSurfaceRadarNotificationEnabled}
          name="notificationEmails"
          placeholder={t('customer.notification_emails_placeholder')}
          values={notificationEmails}
          onChange={handleNotificationEmailsChanged}
        />
      </FieldGroup>

      <FieldGroup>
        <InputLabel htmlFor="notes">{t('customer.notes')}</InputLabel>
        <TextArea
          id="notes"
          name="notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </FieldGroup>

      {isEdit && (
        <FieldGroup>
          <Switch
            onChange={setIsActive}
            checked={isActive}
            id="isActive"
            label={t('customer.active')}
          />
        </FieldGroup>
      )}

      {isEdit && !isActive && <Warning>{t('customer.customer_is_disabled')}</Warning>}

      <SidebarButtons>
        <Button type="button" secondary={true} onClick={close}>
          {t('cancel')}
        </Button>
        <div />
        {(isSaving || isRemoving) && <OperationLoader title={isSaving ? t('saving') + '...' : t('deleting') + '...'} />}
        {isEdit && remove && (
          <RemoveButton type="button" danger={true} disabled={isSaving || isRemoving} onClick={handleRemove}>
            {t('delete')}
          </RemoveButton>
        )}
        <Button type="submit" tertiary={true} disabled={!isValid() || isSaving || isRemoving}>
          {t('save')}
        </Button>
      </SidebarButtons>
    </SideForm>
  );
};

export default CustomerEditor;
